import { useEffect, useMemo } from 'react';
import { useFetcher } from 'react-router';
import { BadgeCheck, Info } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@rocco/components/dialog';
import { cn } from '@rocco/utils/cn';
import { EnquiryFormTrigger } from 'generated/graphql';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { usePageCtasContext } from '~/modules/enquiry/component/PageCtasProvider';
import type { EnquiryProfessional } from '~/modules/enquiry/hooks/use-enquiry';
import {
    PROFESSIONAL_PROJECTS_ROUTE,
    type ProfessionalProjectsResponse,
} from '~/routes/remix-api.professional-projects';
import { DetailTileCard } from './DetailTileCard';
import { SECTION_TITLE_TYPE } from './SectionHead';
import {
    fallbackBiography,
    splitName,
    toParagraphs,
    urlSegmentFromLink,
} from './team-member-text';
import { button } from '~/ui-rocco/button-system';

/** The practice the member belongs to, and who an enquiry reaches. */
export interface TeamMemberOverlayPractice {
    id: number;
    name: string;
    /** Their profile, when the surface knows it. */
    link?: string;
    companyLogo?: string;
    logoBackgroundColor?: string;
    customEnquiryMessage?: string;
    enquiryMessageOverrideEnabled?: boolean;
    enquiryMessagePlaceholder?: string;
    /**
     * The branches an enquiry can be addressed to, where the surface holds
     * them. A profile does; a project credit carries its practices by name
     * rather than by the ids the picker submits, so it leaves this off and the
     * enquiry goes to the practice itself.
     */
    branchOptions?: EnquiryProfessional['branchOptions'];
}

/** The page the overlay was opened from, which is what an enquiry is about. */
export interface TeamMemberOverlaySubject {
    itemType: 'project' | 'professional';
    siteTreeID: number;
    itemTitle?: string;
    relatedItem?: {
        id: number;
        type: 'project';
        title: string;
        category: string;
    };
}

export interface TeamMemberOverlayProps {
    /** The name the tile shows: the person, or the practice when the record has none. */
    displayName: string;
    /** False when the name above is a practice standing in for a missing one. */
    isPerson: boolean;
    jobTitle?: string;
    summary?: string;
    thumbnail?: string;
    licenseNumber?: string;
    practice: TeamMemberOverlayPractice;
    subject: TeamMemberOverlaySubject;
    onBookMeeting?: () => void;
    onClose: () => void;
}

/** The overlay is drawn on the detail pages' dark ground, in their light ink. */
const GROUND = '#181818';
const INK = '#ffffff';

/**
 * The reveal every part of the overlay rises on: a long ease-out that starts
 * fast and settles, so the panel and the name feel like one movement.
 */
const REVEAL_EASE = 'ease-[cubic-bezier(0.16,1,0.3,1)]';

/**
 * A line of the name, masked.
 *
 * The clip is the effect: the line is held in an `overflow-hidden` box and
 * rises into it from below, so it is uncovered rather than moved. Descenders
 * get their own room inside the mask, which is then pulled back off the
 * layout — without it the mask cuts the tail off a `y` or a `g`.
 */
const NameLine = ({
    children,
    className,
    delay,
}: {
    children: React.ReactNode;
    className?: string;
    delay: string;
}) => (
    <span className="block overflow-hidden">
        <span
            className={cn(
                '-mb-[0.14em] block pb-[0.14em]',
                'animate-in duration-[1100ms] fill-mode-both slide-in-from-bottom-full motion-reduce:animate-none',
                REVEAL_EASE,
                delay,
                className,
            )}
        >
            {children}
        </span>
    </span>
);

/** One labelled row in the left column: glyph, then the line it marks. */
const MetaRow = ({
    icon,
    children,
}: {
    icon: React.ReactNode;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-[0.75rem]">
        <span className="mt-[0.0625rem] shrink-0 opacity-70">{icon}</span>
        <div className="min-w-0 leading-[1.2]">{children}</div>
    </div>
);

/**
 * A credited team member, full screen.
 *
 * The portrait holds the centre with the person's name split across the two
 * far corners, their practice and registration down the left with the two
 * actions under it, and their biography set in a column on the right.
 *
 * Every row is drawn from the record and dropped when it is empty, so a member
 * with no biography or no registration gets a shorter column rather than an
 * empty label — the profile's meta rows behave the same way.
 */
export const TeamMemberOverlay = ({
    displayName,
    isPerson,
    jobTitle,
    summary,
    thumbnail,
    licenseNumber,
    practice,
    subject,
    onBookMeeting,
    onClose,
}: TeamMemberOverlayProps) => {
    // Only a person's name straddles the two corners. A practice standing in
    // for a missing one is one phrase, and splitting it reads as two words
    // that happen to be far apart.
    const { first, last } = isPerson
        ? splitName(displayName)
        : { first: displayName, last: '' };
    const written = toParagraphs(summary ?? '');
    const paragraphs = written.length
        ? written
        : [
              fallbackBiography({
                  displayName,
                  isPerson,
                  jobTitle,
                  practiceName: practice.name,
                  canBook: Boolean(onBookMeeting),
              }),
          ];

    // The enquiry goes to the practice that credited this person, which on a
    // project is often not the practice that published it. Branches come from
    // the surface: a profile knows its own, a project credit does not.
    //
    // Keyed on the fields rather than on `practice` and `subject`: both
    // surfaces build them inline, so the objects are new on every render and a
    // memo over them never holds. The enquiry hook treats a new context as a
    // new professional and re-preloads their logo, so the identity has to be
    // as stable as the values are.
    const {
        id: practiceID,
        name: practiceName,
        companyLogo,
        logoBackgroundColor,
        customEnquiryMessage,
        enquiryMessageOverrideEnabled,
        enquiryMessagePlaceholder,
        branchOptions,
    } = practice;
    const { itemType, siteTreeID, itemTitle } = subject;
    const relatedItemID = subject.relatedItem?.id;
    const relatedItemTitle = subject.relatedItem?.title;
    const relatedItemCategory = subject.relatedItem?.category;
    const enquiryContext = useMemo(
        () => ({
            professional: {
                ID: practiceID,
                Title: practiceName,
                branchOptions: branchOptions ?? [],
                CompanyLogo: companyLogo ?? '',
                LogoBackgroundColor: logoBackgroundColor ?? '',
                CustomEnquiryMessage: customEnquiryMessage,
                EnquiryMessageOverrideEnabled: enquiryMessageOverrideEnabled,
                EnquiryMessagePlaceholder: enquiryMessagePlaceholder,
            },
            includeBranch: Boolean(branchOptions?.length),
            itemType,
            siteTreeID,
            itemTitle,
            relatedItem:
                relatedItemID === undefined
                    ? undefined
                    : {
                          id: relatedItemID,
                          type: 'project' as const,
                          title: relatedItemTitle ?? '',
                          category: relatedItemCategory ?? '',
                      },
        }),
        [
            practiceID,
            practiceName,
            companyLogo,
            logoBackgroundColor,
            customEnquiryMessage,
            enquiryMessageOverrideEnabled,
            enquiryMessagePlaceholder,
            branchOptions,
            itemType,
            siteTreeID,
            itemTitle,
            relatedItemID,
            relatedItemTitle,
            relatedItemCategory,
        ],
    );
    // Opted out of the URL trigger: the page already mounts an instance that
    // answers `?enquire`, and a second one would race it with this member.
    const { openEnquiryModal } = useEnquiryModal(enquiryContext, {
        autoOpenFromUrl: false,
    });
    // When the member belongs to the page's own practice, Message is that
    // page's Enquire button: it hides with it and sends its form. A practice
    // credited on someone else's page has no answer here, so it stays open.
    const pageCtas = usePageCtasContext();
    const pageEnquire =
        pageCtas?.enquiry.professional.ID === practiceID
            ? pageCtas.ctas.enquire
            : null;
    const showMessage = pageEnquire?.visible ?? true;

    const practiceSlug = urlSegmentFromLink(practice.link);
    const projects = useFetcher<ProfessionalProjectsResponse>();
    const loadProjects = projects.load;
    useEffect(() => {
        if (!practiceSlug) return;
        loadProjects(
            `${PROFESSIONAL_PROJECTS_ROUTE}?urlSegment=${encodeURIComponent(practiceSlug)}`,
        );
    }, [practiceSlug, loadProjects]);
    const projectTiles = projects.data?.tiles ?? [];

    // The display step is for a person's name, which is two short words. A
    // practice standing in for a missing one is a phrase, and at the display
    // step it runs the width of the frame.
    const nameSize = isPerson
        ? 'text-[clamp(3.5rem,7.75vw,9.3125rem)]'
        : 'text-[clamp(2.25rem,4vw,5rem)]';

    const actions = (
        <div className="flex flex-col gap-[0.5rem]">
            {onBookMeeting ? (
                <button
                    type="button"
                    onClick={onBookMeeting}
                    className={button('primary-inverse', 'm', 'w-full')}
                >
                    Book a Meeting
                </button>
            ) : null}
            {showMessage ? (
                <button
                    type="button"
                    onClick={() =>
                        openEnquiryModal({
                            title: `Message ${practice.name}`,
                            ctaLabel: 'Message',
                            ...(pageEnquire && {
                                trigger: EnquiryFormTrigger.Enquire,
                                formConfig: pageEnquire.formConfig,
                            }),
                        })
                    }
                    className={button('secondary-dark', 'm', 'w-full')}
                >
                    Message
                </button>
            ) : null}
        </div>
    );

    // The practice's mark, on the phone over the portrait's top-left corner
    // rather than in the meta under the name.
    const logoBadge = (className: string) =>
        practice.companyLogo ? (
            <span
                className={cn(
                    'flex items-center justify-center overflow-hidden rounded-full',
                    className,
                )}
                style={{
                    backgroundColor: practice.logoBackgroundColor || '#f7f8f8',
                }}
            >
                <img
                    src={practice.companyLogo}
                    alt={practice.name}
                    className="size-full object-contain p-[1rem]"
                />
            </span>
        ) : null;

    // `compact` is the phone's: no mark (it sits on the portrait), and the
    // role alone rather than the info row with the practice under it.
    const buildMeta = (compact: boolean) => (
        <div className="flex flex-col gap-[1.5rem]">
            {compact ? null : logoBadge('size-[6.4375rem]')}
            <div className="flex flex-col gap-[0.75rem] text-[0.9375rem]">
                {compact ? (
                    jobTitle ? (
                        <p className="m-0 font-medium">{jobTitle}</p>
                    ) : null
                ) : jobTitle || practice.name ? (
                    <MetaRow icon={<Info aria-hidden="true" size={18} />}>
                        {/* The role over the practice it is held at. Dropped
                            rather than filled with the practice when the
                            record has no title: the line below already names
                            it, and standing in printed it twice. */}
                        {jobTitle ? (
                            <p className="m-0 font-medium">{jobTitle}</p>
                        ) : null}
                        {practice.link ? (
                            <a
                                href={practice.link}
                                className="underline underline-offset-2"
                            >
                                {practice.name}
                            </a>
                        ) : (
                            <p className="m-0">{practice.name}</p>
                        )}
                    </MetaRow>
                ) : null}
                {licenseNumber ? (
                    <MetaRow icon={<BadgeCheck aria-hidden="true" size={18} />}>
                        <p className="m-0 font-medium">Registration</p>
                        <p className="m-0">
                            Registration number: {licenseNumber}
                        </p>
                    </MetaRow>
                ) : null}
            </div>
        </div>
    );
    const meta = buildMeta(false);
    const metaMobile = buildMeta(true);

    const biography = paragraphs.length ? (
        <div className="flex flex-col gap-[0.75rem] text-[0.9375rem] leading-[1.35]">
            {/* Keyed on position: the paragraphs are derived from one string
                in one pass and never reordered, and two of them can open on
                the same words — a prefix of the text collides there. */}
            {paragraphs.map((paragraph, index) => (
                <p key={index} className="m-0">
                    {paragraph}
                </p>
            ))}
        </div>
    ) : null;

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                variant="fullScreen"
                spacing="none"
                closeButtonVariant="rounded"
                closeButtonClassName="top-[1.5rem] right-[1.5rem] z-20 size-[2.625rem] shadow-none"
                className={cn(
                    'block overflow-y-auto rounded-none border-0 sm:rounded-none',
                    // The panel comes up from the foot of the window rather
                    // than growing out of the middle: it is opened from a tile
                    // low on the page, and rising is the movement that reads as
                    // continuing from it.
                    'data-[state=open]:animate-in data-[state=open]:duration-[820ms] data-[state=open]:slide-in-from-bottom-full',
                    'data-[state=closed]:animate-out data-[state=closed]:duration-[520ms] data-[state=closed]:slide-out-to-bottom-full',
                    REVEAL_EASE,
                    'motion-reduce:animate-none',
                )}
                style={{ backgroundColor: GROUND, color: INK }}
            >
                <DialogTitle className="sr-only">{displayName}</DialogTitle>
                {/* The fade the design carries across the top, as a gradient
                    rather than an exported vector: it is a wash of the ground,
                    so it has to follow the ground's colour. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[38%] bg-gradient-to-b from-black/[0.06] to-transparent"
                />

                {/* Phone: one column, the portrait first, as every other
                    full-screen sheet on the site reads. */}
                <div className="flex flex-col gap-[1.5rem] px-[1.5rem] pt-[5rem] pb-[2.5rem] md:hidden">
                    {thumbnail ? (
                        <div className="relative">
                            <img
                                src={thumbnail}
                                alt={displayName}
                                className="aspect-[437/524] w-full rounded-tile object-cover opacity-80 grayscale"
                            />
                            {logoBadge('absolute top-4 left-4 size-[3.5rem]')}
                        </div>
                    ) : null}
                    <h2 className="m-0 font-sans text-[2.5rem] leading-[0.9] font-medium tracking-[-0.05em]">
                        <NameLine delay="delay-[260ms]">{first}</NameLine>
                        {last ? (
                            <NameLine delay="delay-[380ms]">{last}</NameLine>
                        ) : null}
                    </h2>
                    {metaMobile}
                    {actions}
                    {biography}
                </div>

                {/* Desktop: the portrait holds the centre, the name straddles
                    it corner to corner, and the two columns sit either side.
                    Positioned against the frame because the name's diagonal is
                    the layout — it cannot be expressed as a flow. */}
                <div className="relative mx-auto hidden h-[100dvh] w-full max-w-(--ap-page-width) md:block">
                    {thumbnail ? (
                        <img
                            src={thumbnail}
                            alt={displayName}
                            className="absolute top-[9%] left-1/2 h-[86%] w-[35.7%] -translate-x-1/2 rounded-tile object-cover opacity-80 grayscale"
                        />
                    ) : null}

                    <h2
                        className={cn(
                            'pointer-events-none absolute top-[8%] left-[3rem] z-10 m-0 font-sans leading-[0.8] font-medium tracking-[-0.05em]',
                            nameSize,
                        )}
                    >
                        <NameLine delay="delay-[260ms]">{first}</NameLine>
                    </h2>
                    {last ? (
                        <p
                            className={cn(
                                'pointer-events-none absolute top-[33%] right-[3rem] z-10 m-0 text-right font-sans leading-[0.8] font-medium tracking-[-0.05em]',
                                nameSize,
                            )}
                        >
                            <NameLine delay="delay-[400ms]">{last}</NameLine>
                        </p>
                    ) : null}

                    <div
                        className={cn(
                            'absolute top-[32%] left-[3rem] z-10 flex w-[19.375rem] flex-col gap-[1.75rem]',
                            'animate-in delay-[560ms] duration-[900ms] fill-mode-both fade-in slide-in-from-bottom-8 motion-reduce:animate-none',
                            REVEAL_EASE,
                        )}
                    >
                        {meta}
                        {actions}
                    </div>

                    {biography ? (
                        <div
                            className={cn(
                                'absolute top-[50%] right-[3rem] z-10 w-[27.1875rem]',
                                'animate-in delay-[680ms] duration-[900ms] fill-mode-both fade-in slide-in-from-bottom-8 motion-reduce:animate-none',
                                REVEAL_EASE,
                            )}
                        >
                            {biography}
                        </div>
                    ) : null}
                </div>

                {projectTiles.length > 0 ? (
                    <section className="mx-auto w-full max-w-(--ap-page-width) max-md:px-[1.5rem] max-md:pb-[4rem] md:px-[3rem] md:pt-[11.25rem] md:pb-[7.5rem]">
                        {/* The person, then their practice — the grid is the
                            practice's, so the line says whose it is before the
                            reader reaches it. Broken after "is" from md so the
                            two lines sit as the page's other heads do; the
                            phone lets it run. */}
                        <h3
                            className={cn('m-0 text-white', SECTION_TITLE_TYPE)}
                        >
                            {isPerson ? `${displayName} is` : 'Projects by'}
                            <br className="max-md:hidden" />
                            <span className="md:hidden"> </span>
                            {isPerson
                                ? `part of ${practice.name}:`
                                : `${practice.name}:`}
                        </h3>
                        <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[3.75rem] md:grid-cols-4 md:gap-x-3 md:gap-y-[6.25rem]">
                            {projectTiles.map((tile) => (
                                <DetailTileCard
                                    key={tile.id}
                                    tile={tile}
                                    theme="dark"
                                />
                            ))}
                        </div>
                    </section>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};
